// src/encounter/nurse-notes-section.tsx
// Pre-encounter CENTER-column "Notes from Nurse" section — the E2 pre-encounter frame's
// "Note" block (1736:156432 → node 1937:183699, 1000×268 at y=433: between Chief Complaint
// and Active Medications; pre-encounter.spec.json). The nurse's pre-visit handoff: author
// identity, the risk-assessment score rows she recorded (Fall / Pain — Flutter
// riskAssessmentWidget reads riskAssessmentData.fallAssessmentData/painAssessmentData), and
// her free-text comment to the physician.
//
// PRESENTATIONAL + HIDDEN WHEN ABSENT (same contract as PreviousNotesSection /
// SubmittedQuestionnaireSection): the section renders ONLY when the host supplies a mapped
// view model — nothing is fabricated. With `?x=nurse-note-card` the page feeds it the visit's Fall
// and Pain assessments (lib/to-nurse-note.ts) as "Risk reassessment" rows with NO author: an entry
// does not record who assessed it. The comment to the physician has no endpoint yet: a nurse sees
// the box marked "not saved yet", disabled, and nothing is sent. Switch off: the page does not
// wire this prop and the app renders no section, as before. DS tokens only; an author, when a
// view has one, falls back to initials (data reality — most records carry no photo).
import { useId, type ReactNode } from 'react';
import { EncounterSectionHeader } from '@scripsteam/scrips-signal-ds';
import { useDesignV2 } from './lib/design-v2';
import { EncBlock, EncSectionCard, useEncSection } from './enc-section-card';

export interface NurseNoteAssessmentRow {
  id: string;
  /** e.g. "Mar 31, 2022 1:45 PM". */
  dateLabel: string;
  /** e.g. "CDC STEADI key questions: at risk (YES to a key question)" or "Pain NRS 0-10 (self-report): 4" (the free
   *  screens — 7 Oct 2026 ruling; an old record may still carry a Morse or Wong-Baker score). */
  scoreLabel: string;
}

export interface NurseNoteAssessment {
  id: string;
  /** e.g. "Fall Assessment". */
  title: string;
  /** Frame marks Fall Assessment with the required asterisk. */
  required?: boolean;
  rows: NurseNoteAssessmentRow[];
}

export interface NurseNotesView {
  /** Who wrote it. Absent: no author line (the Fall / Pain entries do not record who assessed them). */
  authorName?: string;
  authorAvatar?: string;
  /** A title over the assessment rows ("Risk reassessment"). */
  heading?: string;
  assessments: NurseNoteAssessment[];
  /** Free-text handoff comment to the physician. */
  comment?: string;
  /** `unavailable`: the comment box is shown (to a nurse) but cannot be saved — the endpoint is not there. It never makes the card appear. */
  commentBox?: 'unavailable';
  /** The working comment (nurse-comment-box.tsx): read for everyone, edited by the nurse. Its presence draws the card. */
  commentSlot?: ReactNode;
}

export const COMMENT_BOX_LABEL = 'Comment to the physician';
export const COMMENT_NOT_SAVED = 'Not saved yet: comments to the physician cannot be saved until the server supports them.';

/** The comment box while it has no endpoint: labelled, disabled, saying so. Nothing is typed into it, so nothing can be lost. */
function CommentUnavailable() {
  const id = useId();
  return (
    <div data-slot="pv-nurse-comment-box" className="flex flex-col gap-1">
      <label htmlFor={id} className="text-size-sm font-medium text-text-primary">
        {COMMENT_BOX_LABEL}
      </label>
      <textarea
        id={id}
        disabled
        rows={2}
        aria-describedby={`${id}-note`}
        className="w-full resize-none rounded-md border border-border-default bg-surface-panel p-2 text-size-sm text-text-secondary"
      />
      <p id={`${id}-note`} className="m-0 text-size-xs text-text-secondary">
        {COMMENT_NOT_SAVED}
      </p>
    </div>
  );
}

const CARD =
  'bg-surface-card border border-border-default ' +
  'rounded-lg p-6';
const INNER =
  'border border-border-default rounded-md ' +
  'p-4 flex flex-col gap-3';
const AUTHOR_ROW = 'flex items-center gap-2';
// w-7/h-7 (28px): Tailwind's spacing scale step (7 * 4px) is an exact match, not an approximation.
const AVATAR =
  'w-8 h-8 rounded-full bg-status-info-bg text-text-secondary ' +
  'flex items-center justify-center text-size-xs font-bold shrink-0 overflow-hidden';
const AUTHOR = 'text-size-sm font-semibold text-text-primary';
const ASSESS_TITLE = 'm-0 text-size-sm font-bold text-text-primary';
const ROW = 'flex items-baseline gap-4 text-size-sm';
const DATE = 'text-text-hint whitespace-nowrap';
const SCORE = 'text-brand-primary font-medium';
const COMMENT = 'm-0 text-size-sm text-text-primary whitespace-pre-wrap';
const STACK_1 = 'flex flex-col gap-1';

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase() || '?';
}

export function NurseNotesSection({ notes }: { notes?: NurseNotesView | null }) {
  const designV2 = useDesignV2();
  const inCard = useEncSection() !== null;
  // No nurse handoff recorded → section hidden (never an empty shell).
  if (!notes) return null;
  const hasBody = notes.assessments.length > 0 || !!notes.comment?.trim() || notes.commentSlot != null;
  if (!hasBody) return null;

  // Frame v2 (design export 21-10, EncPreVisitMain): one Subjective block of the pre-visit card; outside a card it brings its own.
  if (designV2) {
    const block = (
      <EncBlock title="Notes from nurse" icon="docs" section="s" aria-label="Notes from nurse">
        <div data-slot="pv-nurse">
          {notes.authorName ? (
            <div data-slot="pv-nurse-author">
              <span data-slot="pv-nurse-avatar" aria-hidden="true">
                {notes.authorAvatar ? <img src={notes.authorAvatar} alt="" /> : initials(notes.authorName)}
              </span>
              <span data-slot="pv-nurse-name">{notes.authorName}</span>
            </div>
          ) : null}
          {notes.heading ? <p data-slot="pv-nurse-heading">{notes.heading}</p> : null}
          {notes.assessments.map((a) => (
            <div key={a.id} data-slot="pv-nurse-assessment">
              <p data-slot="pv-nurse-title">
                {a.title}
                {a.required && <span data-slot="pv-req"> *</span>}
              </p>
              {a.rows.map((r) => (
                <div key={r.id} data-slot="pv-nurse-row">
                  <span data-slot="pv-nurse-date">{r.dateLabel}</span>
                  <span data-slot="pv-nurse-score">{r.scoreLabel}</span>
                </div>
              ))}
            </div>
          ))}
          {notes.comment?.trim() && <p data-slot="pv-nurse-comment">{notes.comment.trim()}</p>}
          {notes.commentBox === 'unavailable' ? <CommentUnavailable /> : null}
          {notes.commentSlot}
        </div>
      </EncBlock>
    );
    return inCard ? block : <EncSectionCard section="s">{block}</EncSectionCard>;
  }

  return (
    <section className={CARD} aria-label="Notes from nurse">
      <EncounterSectionHeader title="Notes from Nurse" variant="section" />
      <div className="h-3" />
      <div className={INNER}>
        {notes.authorName ? (
          <div className={AUTHOR_ROW}>
            <span className={AVATAR} aria-hidden="true">
              {notes.authorAvatar ? (
                <img src={notes.authorAvatar} alt="" className="w-full h-full object-cover" />
              ) : (
                initials(notes.authorName)
              )}
            </span>
            <span className={AUTHOR}>{notes.authorName}</span>
          </div>
        ) : null}
        {notes.heading ? <p className={ASSESS_TITLE}>{notes.heading}</p> : null}
        {notes.assessments.map((a) => (
          <div key={a.id} className={STACK_1}>
            <p className={ASSESS_TITLE}>
              {a.title}
              {a.required && <span className="text-status-error"> *</span>}
            </p>
            {a.rows.map((r) => (
              <div key={r.id} className={ROW}>
                <span className={DATE}>{r.dateLabel}</span>
                <span className={SCORE}>{r.scoreLabel}</span>
              </div>
            ))}
          </div>
        ))}
        {notes.comment?.trim() && <p className={COMMENT}>{notes.comment.trim()}</p>}
        {notes.commentBox === 'unavailable' ? <CommentUnavailable /> : null}
        {notes.commentSlot}
      </div>
    </section>
  );
}
